import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { type Dayjs } from 'dayjs';
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { CheckboxPopover } from '@/ui/composites/filters/CheckboxPopover';
import { DropdownFiltersPopover } from '@/ui/composites/filters/DropdownFiltersPopover';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { Box, Cancel, IconButton, Text, Tooltip, type Theme, useTheme } from '@/ui/primitives';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from './cloneTableConstants';
import { CloneTableDatePopover } from './CloneTableDatePopover';
import { CloneTableSortPopover, type CloneSortOption } from './CloneTableSortPopover';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
  resolveCloneDatePreview,
} from './cloneTableToolbar.helpers';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

type CloneToolbarPopover = 'columns' | 'filters' | 'date' | 'sort' | null;
type ToolbarHoverTarget = 'columns' | 'filters' | 'date' | 'sort' | 'search' | 'reset' | null;
export type CloneColumnOption = { field: string; label: string; mandatory?: boolean };
export type CloneStatusOption<TStatus extends string> = { value: TStatus; label: string };
export type CloneDropdownOption = { value: string; label: string };
const CLONE_TABLE_HEADER_TOOLBAR_BOTTOM_PADDING = 8;
export type CloneTableToolbarProps<TSortKey extends string, TStatus extends string> = {
  title?: string;
  searchAriaLabel: string;
  onDebouncedCloneSearchChange: (value: string) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
  columnOptions: CloneColumnOption[];
  onColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  sortKey: TSortKey;
  sortDirection: 'asc' | 'desc';
  defaultSortKey: TSortKey;
  defaultSortDirection: 'asc' | 'desc';
  sortOptions: Array<CloneSortOption<TSortKey>>;
  onSortChange: (sortKey: TSortKey, sortDirection: 'asc' | 'desc') => void;
  filterStatuses: TStatus[];
  statusOptions: Array<CloneStatusOption<TStatus>>;
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterSuites?: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: CloneDropdownOption[];
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  dateQuickFilter: CloneDateQuickFilter;
  onDateQuickFilterChange: (next: CloneDateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  availableTags: string[];
  includeLatestQuickFilter?: boolean;
  statusFilterLabel?: string;
};
const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: (theme: Theme) => theme.typography.body2.fontSize,
      lineHeight: (theme: Theme) => theme.typography.body2.lineHeight,
    },
  },
} as const;

export const CloneTableToolbar = <TSortKey extends string, TStatus extends string>({
  title,
  searchAriaLabel,
  onDebouncedCloneSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  sortOptions,
  onSortChange,
  filterStatuses,
  statusOptions,
  onFilterStatusesChange,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  availableTags,
  includeLatestQuickFilter = false,
  statusFilterLabel = 'Latest Status',
}: CloneTableToolbarProps<TSortKey, TStatus>): ReactElement => {
  const theme = useTheme();
  const toolbarControlSize = 32;
  const toolbarExpandedSearchWidth = 252;
  const toolbarSearchWidthPx = `${toolbarExpandedSearchWidth}px`;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const [popoverState, setPopoverState] = useState<{
    type: CloneToolbarPopover;
    anchorEl: HTMLElement | null;
  }>({
    type: null,
    anchorEl: null,
  });
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [hoverTarget, setHoverTarget] = useState<ToolbarHoverTarget>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRestoreTimeoutRef = useRef<number | null>(null);
  const openPopover =
    (type: Exclude<CloneToolbarPopover, null>) => (event: MouseEvent<HTMLElement>) => {
      setPopoverState({ type, anchorEl: event.currentTarget });
    };
  const closePopover = (): void => {
    setPopoverState({ type: null, anchorEl: null });
  };
  const preserveWindowScroll = (action: () => void): void => {
    if (typeof window === 'undefined') {
      action();
      return;
    }
    const { scrollX, scrollY } = window;
    action();
    window.requestAnimationFrame(() => {
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
    });
    if (scrollRestoreTimeoutRef.current !== null) {
      window.clearTimeout(scrollRestoreTimeoutRef.current);
    }
    scrollRestoreTimeoutRef.current = window.setTimeout(() => {
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      scrollRestoreTimeoutRef.current = null;
    }, 150);
  };
  useEffect(() => {
    const timer = window.setTimeout(() => {
      onDebouncedCloneSearchChange(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onDebouncedCloneSearchChange, searchValue]);
  useEffect(() => {
    if (!searchExpanded) return;
    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, [searchExpanded]);
  useEffect(
    () => () => {
      if (scrollRestoreTimeoutRef.current !== null) {
        window.clearTimeout(scrollRestoreTimeoutRef.current);
      }
    },
    [],
  );
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const toolbarIconTooltipDelayMs = 250;
  const hasNonDefaultColumns = columnOptions.some((option) => {
    if (option.mandatory) return false;
    const currentVisible = columnVisibilityModel[option.field];
    const baselineVisible = defaultColumnVisibilityModel[option.field];
    return currentVisible !== baselineVisible;
  });
  const hasActiveFilters =
    filterStatuses.length > 0 || filterSuites.length > 0 || filterTags.length > 0;
  const hasActiveSearch = searchValue.trim().length > 0;
  const defaultDateQuickFilter: CloneDateQuickFilter = '7d';
  const hasActiveDateFilters =
    dateQuickFilter !== defaultDateQuickFilter || fromDate !== null || toDate !== null;
  const hasActiveSort = sortKey !== defaultSortKey || sortDirection !== defaultSortDirection;
  const hasActiveToolbarState =
    hasActiveFilters ||
    hasActiveDateFilters ||
    hasActiveSort ||
    hasNonDefaultColumns ||
    hasActiveSearch;
  const { displayedFromDate, displayedToDate } = resolveCloneDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
  );
  const dateRangeLabel =
    dateQuickFilter === 'latest' && fromDate === null && toDate === null
      ? 'Showing latest executions'
      : displayedFromDate && displayedToDate
        ? `${displayedFromDate.format('MMM D, YYYY')} - ${displayedToDate.format('MMM D, YYYY')}`
        : null;
  const resetToolbarFilters = (): void => {
    preserveWindowScroll(() => {
      setSearchValue('');
      setSearchExpanded(false);
      onFilterStatusesChange([]);
      onFilterSuitesChange?.([]);
      onFilterTagsChange([]);
      onDateQuickFilterChange(defaultDateQuickFilter);
      onFromDateChange(null);
      onToDateChange(null);
      onSortChange(defaultSortKey, defaultSortDirection);
      onColumnVisibilityModelChange({
        ...defaultColumnVisibilityModel,
        name: true,
      });
      closePopover();
    });
  };
  return (
    <Stack
      direction="row"
      justifyContent={title ? 'space-between' : 'flex-end'}
      alignItems="center"
      gap="md"
      sx={{
        width: '100%',
        minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
        height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      {title ? (
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: s('sm'),
          }}
        >
          <Text
            role="bodyStrong"
            sx={{
              fontSize: (muiTheme) => muiTheme.typography.h6.fontSize,
              lineHeight: (muiTheme) => muiTheme.typography.h6.lineHeight,
            }}
          >
            {title}
          </Text>
        </Box>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        gap="sm"
        sx={{
          minWidth: 0,
          height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          boxSizing: 'border-box',
          pb: title ? undefined : `${CLONE_TABLE_HEADER_TOOLBAR_BOTTOM_PADDING}px`,
        }}
      >
        {dateRangeLabel ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pr: s('sm'),
            }}
          >
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.body2.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.body2.lineHeight,
              }}
            >
              {dateRangeLabel}
            </Text>
          </Box>
        ) : null}
        <Tooltip
          title="Date Filters"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open date filters"
            onClick={openPopover('date')}
            onMouseEnter={() => {
              setHoverTarget('date');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'date')}>
              <EventOutlinedIcon
                sx={{
                  color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor,
                  width: '18px !important',
                  height: '18px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Columns"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open columns"
            onClick={openPopover('columns')}
            onMouseEnter={() => {
              setHoverTarget('columns');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'columns')}>
              <ViewColumnOutlinedIcon
                sx={{
                  color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor,
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Filters"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open filters"
            onClick={openPopover('filters')}
            onMouseEnter={() => {
              setHoverTarget('filters');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'filters')}>
              <FilterListOutlinedIcon
                sx={{
                  color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor,
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Sort"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open sort"
            onClick={openPopover('sort')}
            onMouseEnter={() => {
              setHoverTarget('sort');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'sort')}>
              <SortOutlinedIcon
                sx={{
                  color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor,
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            width: searchExpanded ? toolbarExpandedSearchWidth : 32,
            height: toolbarControlSize,
            minHeight: toolbarControlSize,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: (t) =>
              t.transitions.create('width', {
                duration: t.transitions.duration.shorter,
                easing: t.transitions.easing.easeInOut,
              }),
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              width: '100%',
              height: `${toolbarControlSize}px`,
              transform: searchExpanded
                ? 'translateY(-50%) translateX(0)'
                : 'translateY(-50%) translateX(-8px)',
              opacity: searchExpanded ? 1 : 0,
              pointerEvents: searchExpanded ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              transition: (t) =>
                t.transitions.create(['opacity', 'transform'], {
                  duration: t.transitions.duration.shorter,
                  easing: t.transitions.easing.easeInOut,
                }),
            }}
          >
            <SearchFilterField
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
              placeholder="Search..."
              inputAriaLabel={searchAriaLabel}
              controlHeight={32}
              compact
              leadingSearchAdornment
              trailingSearchAdornment={false}
              filledClearIcon={false}
              sx={{
                width: toolbarSearchWidthPx,
                minWidth: toolbarSearchWidthPx,
                maxWidth: toolbarSearchWidthPx,
                '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                  height: `${toolbarControlSize}px !important`,
                },
                '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
                  height: `${toolbarControlSize}px !important`,
                  paddingLeft: `${s('xs')} !important`,
                  paddingInlineStart: `${s('xs')} !important`,
                },
              }}
              slotProps={{
                htmlInput: {
                  ref: (instance: HTMLInputElement | null) => {
                    searchInputRef.current = instance;
                  },
                  onBlur: () => {
                    if (searchValue.trim().length > 0) return;
                    setSearchExpanded(false);
                  },
                  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== 'Escape') return;
                    if (searchValue.length > 0) {
                      setSearchValue('');
                      return;
                    }
                    setSearchExpanded(false);
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{
              opacity: searchExpanded ? 0 : 1,
              pointerEvents: searchExpanded ? 'none' : 'auto',
              transform: searchExpanded ? 'scale(0.9)' : 'scale(1)',
              transition: (t) =>
                t.transitions.create(['opacity', 'transform'], {
                  duration: t.transitions.duration.shorter,
                  easing: t.transitions.easing.easeInOut,
                }),
            }}
          >
            <Tooltip
              title="Search"
              arrow
              enterDelay={toolbarIconTooltipDelayMs}
              slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            >
              <IconButton
                size="medium"
                aria-label="Open search"
                onClick={() => {
                  setSearchExpanded(true);
                }}
                sx={[
                  toolbarIconButtonSx,
                  {
                    width: toolbarControlSize,
                    height: toolbarControlSize,
                    minWidth: toolbarControlSize,
                  },
                ]}
              >
                <Box sx={toolbarIconCircleSx(hoverTarget === 'search')}>
                  <SearchOutlinedIcon
                    sx={{
                      color: toolbarInactiveIconColor,
                      width: '20px !important',
                      height: '20px !important',
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Tooltip
          title="Reset"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
          >
            <IconButton
              size="medium"
              aria-label="Reset filters"
              onClick={resetToolbarFilters}
              disabled={!hasActiveToolbarState}
              sx={[toolbarIconButtonSx, hasActiveToolbarState ? { color: 'primary.main' } : {}]}
            >
              <Box sx={toolbarIconCircleSx(hoverTarget === 'reset' && hasActiveToolbarState)}>
                <Cancel sx={{ width: '20px !important', height: '20px !important' }} />
              </Box>
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
      <CheckboxPopover
        open={popoverState.type === 'columns'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Columns"
        headingIcon={
          <ViewColumnOutlinedIcon sx={{ width: '16px !important', height: '16px !important' }} />
        }
        minWidth={300}
        sections={[
          {
            key: 'columns',
            options: columnOptions.map((option) => {
              const checked = option.mandatory ? true : columnVisibilityModel[option.field];
              return {
                key: option.field,
                label: option.label,
                checked,
                disabled: Boolean(option.mandatory),
                onChange: (nextChecked: boolean) => {
                  if (option.mandatory) return;
                  preserveWindowScroll(() => {
                    onColumnVisibilityModelChange({
                      ...columnVisibilityModel,
                      [option.field]: nextChecked,
                      name: true,
                    });
                  });
                },
              };
            }),
          },
        ]}
      />
      <DropdownFiltersPopover
        open={popoverState.type === 'filters'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Filters"
        headingIcon={
          <FilterListOutlinedIcon sx={{ width: '16px !important', height: '16px !important' }} />
        }
        selectedStatuses={filterStatuses}
        onStatusesChange={(next) => {
          preserveWindowScroll(() => {
            onFilterStatusesChange(next as TStatus[]);
          });
        }}
        statusOptions={statusOptions}
        statusLabel={statusFilterLabel}
        selectedSuites={filterSuites}
        onSuitesChange={
          onFilterSuitesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterSuitesChange(next);
                });
              }
            : undefined
        }
        suiteOptions={suiteOptions}
        selectedTags={filterTags}
        onTagsChange={(next) => {
          preserveWindowScroll(() => {
            onFilterTagsChange(next);
          });
        }}
        tagOptions={availableTags.map((tag) => ({ value: tag, label: tag }))}
      />
      <CloneTableDatePopover
        open={popoverState.type === 'date'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        dateQuickFilter={dateQuickFilter}
        onDateQuickFilterChange={(next) => {
          preserveWindowScroll(() => {
            onDateQuickFilterChange(next);
          });
        }}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={(next) => {
          preserveWindowScroll(() => {
            onFromDateChange(next);
          });
        }}
        onToDateChange={(next) => {
          preserveWindowScroll(() => {
            onToDateChange(next);
          });
        }}
        includeLatestQuickFilter={includeLatestQuickFilter}
      />
      <CloneTableSortPopover
        open={popoverState.type === 'sort'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Stack>
  );
};

export type { CloneDateQuickFilter };
